import {Component, effect, inject, signal, ViewEncapsulation} from '@angular/core';
import {NavigationEnd, NavigationError, Router, RouterModule} from '@angular/router';
import {filter} from 'rxjs/operators';
import {HeaderComponent} from './layout/header/header.component';
import {FooterComponent} from './layout/footer/footer.component';
import {Toast} from 'primeng/toast';
import {AsyncPipe, NgClass} from '@angular/common';
import {StagingLayoutService} from './core/staging-layout.service';
import {StagingBarComponent} from './shared/components/staging-bar/staging-bar.component';
import {ConfirmationDialogComponent} from './shared/components/dialog/confirmation/confirmation-dialog.component';
import {ContentWrapperComponent} from './shared/components/content-wrapper/content-wrapper.component';
import {KEYCLOAK_EVENT_SIGNAL, KeycloakEventType, ReadyArgs, typeEventArgs} from 'keycloak-angular';
import {TranslocoService} from '@jsverse/transloco';
import {UserStateService} from './shared/services/user-state.service';
import {UserlaneService} from './shared/services/userlane.service';
import {ChatbotWidgetComponent} from './shared/chatbot/chatbot-widget.component';
import {DebugDialogService} from './shared/debug/service/debug-dialog.service';
import {LoadingComponent} from './shared/components/loading/loading.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  encapsulation: ViewEncapsulation.None,
  imports: [
    RouterModule,
    HeaderComponent,
    FooterComponent,
    Toast,
    NgClass,
    AsyncPipe,
    StagingBarComponent,
    ConfirmationDialogComponent,
    ContentWrapperComponent,
    ChatbotWidgetComponent,
    LoadingComponent,
  ],
})
export class AppComponent {
  private static readonly CHUNK_RELOAD_KEY = 'app.chunkReloadUrl';

  title = 'Selecthor';
  private readonly stagingLayoutService = inject(StagingLayoutService);
  private readonly router = inject(Router);
  private readonly keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL);
  private readonly translocoService = inject(TranslocoService);
  private readonly userStateService = inject(UserStateService);
  private readonly userlaneService = inject(UserlaneService);
  private readonly debugDialogService = inject(DebugDialogService);

  showLayout$ = this.stagingLayoutService.showLayout$;
  isLoading = signal(true);
  isAuthenticated = signal(false);

  constructor() {
    this.setupChunkLoadRecovery();

    effect(async () => {
      const keycloakEvent = this.keycloakSignal();
      if (keycloakEvent.type === KeycloakEventType.Ready) {
        const isAuthenticated = typeEventArgs<ReadyArgs>(keycloakEvent.args);
        this.isAuthenticated.set(isAuthenticated);
        if (isAuthenticated) {
          await this.loadUserLanguagePreference();
        }

        await this.initializeUserlane();
        this.isLoading.set(false);
      }
    });
  }

  /**
   * Recovers from stale lazy-chunk loads. After a new deploy (or many dev hot-reloads) an open tab
   * still references old chunk hashes; navigating to a lazy route then fails with a "failed to fetch
   * dynamically imported module" error. We reload the intended URL once so the browser fetches the
   * current index + chunk hashes, guarding against reload loops via sessionStorage.
   */
  private setupChunkLoadRecovery(): void {
    this.router.events
      .pipe(filter((event): event is NavigationError => event instanceof NavigationError))
      .subscribe((event) => {
        if (!this.isChunkLoadError(event.error)) {
          return;
        }
        if (sessionStorage.getItem(AppComponent.CHUNK_RELOAD_KEY) === event.url) {
          return;
        }
        sessionStorage.setItem(AppComponent.CHUNK_RELOAD_KEY, event.url);
        window.location.assign(event.url);
      });

    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => sessionStorage.removeItem(AppComponent.CHUNK_RELOAD_KEY));
  }

  private isChunkLoadError(error: unknown): boolean {
    const message = error instanceof Error ? error.message : String(error);
    return /ChunkLoadError|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i.test(
      message,
    );
  }

  private async initializeUserlane(): Promise<void> {
    const currentLanguage = this.translocoService.getActiveLang();
    await this.userlaneService.initialize(currentLanguage);
  }

  isProjectStepsRoute(): boolean {
    return this.router.url.includes('/steps');
  }

  private async loadUserLanguagePreference(): Promise<void> {
    try {
      const languageCode = await this.getCurrentUserLanguageCode();

      if (languageCode) {
        const currentLang = this.translocoService.getActiveLang();
        if (currentLang !== languageCode) {
          this.translocoService.setActiveLang(languageCode);
        }
      }
    } catch (error) {
      console.warn('Could not load user language preference:', error);
    }
  }

  private async getCurrentUserLanguageCode(): Promise<string | null> {
    try {
      return this.userStateService.getCurrentUserLanguageCode();
    } catch (error) {
      console.warn('Could not get user language code:', error);
      return null;
    }
  }
}
